<template>
  <v-dialog v-model="show" fullscreen transition="dialog-bottom-transition">
    <v-card class="bg-dark">
      <v-toolbar dark color="#1e1e1e" elevation="10">
        <v-btn icon dark @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="font-weight-bold">Kennzeichen Kontrolle</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="loadData" :loading="loading" v-if="!showingGlobalResults">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-6" style="background-color: #121212; min-height: 100vh">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Name oder Kennzeichen suchen..."
          outlined
          dark
          color="#4CAF50"
          hide-details
          class="mb-4 rounded-lg glass-input"
          clearable></v-text-field>

        <v-select
          v-model="sortBy"
          :items="sortOptions"
          item-text="text"
          item-value="value"
          label="Sortieren nach"
          outlined
          dark
          color="#4CAF50"
          hide-details
          class="mb-4 rounded-lg glass-input"
          prepend-inner-icon="mdi-sort"
          v-if="!showingGlobalResults"></v-select>

        <v-switch
          v-model="showWithoutPlates"
          label="Personen ohne Kennzeichen anzeigen"
          color="#4CAF50"
          dark
          hide-details
          class="mb-6 mt-0"
          v-if="!showingGlobalResults"></v-switch>

        <v-skeleton-loader v-if="loading || globalLoading" type="list-item-avatar-two-line@5" dark class="transparent-skeleton"></v-skeleton-loader>

        <div v-else-if="filteredEntries.length === 0 && !showingGlobalResults">
          <v-alert type="info" text color="grey" class="rounded-lg mb-4">
            <div v-if="search && search.trim().length >= 2" class="d-flex align-center">
              <v-progress-circular indeterminate size="20" width="2" color="grey" class="mr-3"></v-progress-circular>
              <span>Durchsuche automatisch die gesamte Datenbank...</span>
            </div>
            <div v-else>Keine eingecheckte Person gefunden.</div>
          </v-alert>
        </div>

        <v-alert v-else-if="filteredEntries.length === 0 && showingGlobalResults" type="warning" text color="orange" class="rounded-lg">
          Auch in der Datenbank wurde niemand gefunden.
        </v-alert>

        <v-alert
          v-if="showingGlobalResults && filteredEntries.length > 0"
          border="left"
          colored-border
          color="#4CAF50"
          elevation="2"
          dark
          class="mb-4"
          style="background: rgba(76, 175, 80, 0.1)">
          <div class="d-flex align-center w-100">
            <div>
              <div class="font-weight-bold white--text">Datenbank-Treffer</div>
              <div class="caption grey--text">Diese Personen sind aktuell nicht eingecheckt.</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn small text color="#4CAF50" @click="clearGlobalSearch"> <v-icon left small>mdi-arrow-left</v-icon> Zurück </v-btn>
          </div>
        </v-alert>

        <div v-if="!(loading || globalLoading) && filteredEntries.length > 0">
          <v-card v-for="(entry, index) in filteredEntries" :key="entry._id + '-' + index" class="mb-3 glass-panel rounded-xl" dark elevation="0">
            <div class="d-flex align-center pa-4">
              <v-avatar size="50" color="grey darken-3" class="mr-4 elevation-5" style="border: 2px solid rgba(255, 255, 255, 0.1)">
                <img v-if="entry.user && entry.user.bild" :src="'/img/profilbilder/' + entry.user.bild" />
                <span v-else class="white--text font-weight-bold text-h6">{{ getInitials(entry) }}</span>
              </v-avatar>

              <div class="flex-grow-1 overflow-hidden pr-2">
                <div class="font-weight-bold white--text text-truncate" style="font-size: 1.1rem">{{ entry.firstName }} {{ entry.lastName }}</div>
                <div class="caption grey--text text--lighten-1 mt-1 d-flex align-center">
                  <template v-if="!showingGlobalResults">
                    <v-icon x-small color="grey" class="mr-1">mdi-login</v-icon>
                    {{ formatTime(entry.checkinDate) }} Uhr
                  </template>
                  <template v-else>
                    <v-icon x-small color="blue-grey" class="mr-1">mdi-database</v-icon>
                    Datenbank Treffer
                  </template>
                </div>
              </div>

              <div class="d-flex flex-column align-end flex-shrink-0">
                <template v-if="entry.displayPlate">
                  <div class="license-plate elevation-2 mb-2">
                    <div class="plate-text">{{ formatPlate(entry.displayPlate) }}</div>
                  </div>
                </template>
                <v-chip v-else small outlined color="grey darken-1" class="font-weight-medium mb-2"> Kein Kennzeichen </v-chip>

                <v-btn small color="rgba(76, 175, 80, 0.15)" class="green--text text--lighten-1" elevation="0" rounded @click="selectUser(entry)">
                  <v-icon left small>mdi-cursor-default-click</v-icon> Auswählen
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios"
import moment from "moment"

export default {
  name: "LicensePlateList",
  props: {
    value: Boolean,
  },
  data() {
    return {
      loading: false,
      search: "",
      searchTimeout: null,
      showWithoutPlates: false,
      entries: [],

      // Global Search State
      globalEntries: [],
      globalLoading: false,
      showingGlobalResults: false,

      // Sortierung
      sortBy: "plateAsc",
      sortOptions: [
        { text: "Kennzeichen (1-9)", value: "plateAsc" },
        { text: "Name (A-Z)", value: "nameAsc" },
        { text: "Check-in (Neueste zuerst)", value: "timeDesc" },
      ],
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
    filteredEntries() {
      const source = this.showingGlobalResults ? this.globalEntries : this.entries
      let flattenedList = []

      source.forEach((e) => {
        const validPlates = e.user && e.user.licensePlates ? e.user.licensePlates.filter((p) => p && p.trim() !== "") : []

        if (validPlates.length > 0) {
          validPlates.forEach((plate) => {
            flattenedList.push({ ...e, displayPlate: plate })
          })
        } else if (!this.showingGlobalResults && this.showWithoutPlates) {
          flattenedList.push({ ...e, displayPlate: null })
        }
      })

      if (this.search && !this.showingGlobalResults) {
        const searchRaw = this.search.toLowerCase()
        const searchNoSpaces = searchRaw.replace(/\s/g, "")

        flattenedList = flattenedList.filter((e) => {
          const name = `${e.firstName} ${e.lastName}`.toLowerCase()
          const plateNoSpaces = e.displayPlate ? e.displayPlate.toLowerCase().replace(/\s/g, "") : ""
          return name.includes(searchRaw) || plateNoSpaces.includes(searchNoSpaces)
        })
      }

      if (!this.showingGlobalResults) {
        flattenedList.sort((a, b) => {
          if (this.sortBy === "plateAsc") {
            // Neue alphabetische / lexikografische Sortierung der Zahlen
            const numStrA = this.extractPlateString(a.displayPlate)
            const numStrB = this.extractPlateString(b.displayPlate)

            if (numStrA === numStrB) {
              // Wenn die Zahlen gleich sind (oder beides ohne Kennzeichen), nach ganzem String sortieren
              const fullA = (a.displayPlate || "zzzzzzzzz").toLowerCase()
              const fullB = (b.displayPlate || "zzzzzzzzz").toLowerCase()
              return fullA.localeCompare(fullB)
            }

            return numStrA.localeCompare(numStrB)
          } else if (this.sortBy === "nameAsc") {
            const nameA = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase()
            const nameB = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase()
            return nameA.localeCompare(nameB)
          } else if (this.sortBy === "timeDesc") {
            const timeA = new Date(a.checkinDate).getTime()
            const timeB = new Date(b.checkinDate).getTime()
            return timeB - timeA
          }
          return 0
        })
      }

      return flattenedList
    },
  },
  watch: {
    search(newVal) {
      if (this.showingGlobalResults) {
        this.clearGlobalSearch()
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      if (newVal && newVal.trim().length >= 2) {
        this.searchTimeout = setTimeout(() => {
          if (this.filteredEntries.length === 0 && !this.showingGlobalResults) {
            this.searchGlobalDb()
          }
        }, 500)
      }
    },
    show(newVal) {
      if (newVal) {
        this.clearGlobalSearch()
        this.search = ""
        this.loadData()
      }
    },
  },
  methods: {
    selectUser(entry) {
      if (!entry.user || !entry.user._id) {
        this.$toast.warning("Dieser Eintrag ist noch keinem App-Account zugeordnet.")
        return
      }

      this.show = false
      this.$emit("select-user", entry.user)
    },
    async loadData() {
      this.loading = true
      try {
        const res = await axios.get("/api/checkin/pool/entries/new", {
          params: { filterCheckedout: true, limit: 1000, page: 1 },
          headers: { Authorization: localStorage.getItem("token") },
        })

        if (res.data && res.data.entries) {
          this.entries = res.data.entries
        }
      } catch (e) {
        console.error("Fehler beim Laden der Checkins", e)
      } finally {
        this.loading = false
      }
    },
    async searchGlobalDb() {
      if (!this.search || this.search.trim().length < 2) return

      this.globalLoading = true
      try {
        const res = await axios.get("/api/user/search/license-plates", {
          params: { q: this.search },
          headers: { Authorization: localStorage.getItem("token") },
        })

        if (res.data && res.data.users) {
          this.globalEntries = res.data.users.map((u) => ({
            _id: u._id,
            firstName: u.vorname,
            lastName: u.nachname,
            checkinDate: null,
            user: {
              _id: u._id,
              bild: u.bild,
              licensePlates: u.licensePlates,
            },
          }))
          this.showingGlobalResults = true
        }
      } catch (e) {
        console.error("Fehler bei globaler Suche", e)
        this.$toast.error("Fehler bei der Datenbank-Suche")
      } finally {
        this.globalLoading = false
      }
    },
    clearGlobalSearch() {
      this.showingGlobalResults = false
      this.globalEntries = []
    },
    formatPlate(plate) {
      if (!plate) return ""
      let p = plate.replace(/\s/g, "")
      if (p.length > 2) {
        return p.substring(0, 2) + " " + p.substring(2)
      }
      return p
    },
    extractPlateString(plateStr) {
      // Extrahiert die Zahl, wandelt sie aber NICHT in Integer um, sondern lässt sie als String
      if (!plateStr) return "zzzzzzzzz" // Schiebt leere Einträge ans Ende
      const match = plateStr.match(/\d+/)
      return match ? match[0] : "zzzzzzzzz"
    },
    getInitials(entry) {
      const f = entry.firstName ? entry.firstName.charAt(0) : ""
      const l = entry.lastName ? entry.lastName.charAt(0) : ""
      return (f + l).toUpperCase()
    },
    formatTime(dateStr) {
      if (!dateStr) return ""
      return moment(dateStr).format("HH:mm")
    },
  },
}
</script>

<style lang="scss" scoped>
/* Glassmorphism Design */
.glass-panel {
  background: rgba(30, 30, 30, 0.6) !important;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.glass-input {
  background: rgba(255, 255, 255, 0.03);
}
.transparent-skeleton {
  background: transparent !important;
  ::v-deep .v-skeleton-loader__bone {
    background: rgba(255, 255, 255, 0.05);
  }
}

.w-100 {
  width: 100%;
}

/* Nummernschild Styling */
.license-plate {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #9e9e9e;
  height: 28px;
}

.plate-text {
  color: #212121;
  font-family: "Courier New", Courier, monospace;
  font-weight: 900;
  font-size: 0.95rem;
  padding: 0 8px;
  letter-spacing: 1.5px;
  white-space: nowrap;
}
</style>
